/**
 * Reads a refusal that arrived as a Blob back into the JSON it was sent as.
 *
 * An endpoint requested with `responseType: 'blob'` gets its ERROR body as a
 * Blob too: axios applies the response type to every answer, not only to the
 * successful one. The server's `{ message, reason, detail }` therefore reached
 * `messageOf` as an opaque Blob, no reason was found, and the invoice preview
 * of a booking without a customer address said only "Die Vorschau konnte nicht
 * erstellt werden" — while "Rechnung erstellen", a JSON request, named the
 * missing fields.
 *
 * Used as `transformErrorResponse` on every blob endpoint, so the error shape
 * matches the JSON endpoints and one message mapping serves both. Only a JSON
 * refusal of ours is taken over: an object carrying a `reason`, `code` or
 * `detail`. Anything else (a proxy's HTML 502 page, an empty body, JSON
 * without a reason) leaves `data` empty, so the screen shows its own generic
 * failure line rather than a raw page. The status stays, so it is still a
 * failure.
 *
 * Its own module, importing nothing of ours, so a test can reach it without
 * the slice's axios/Auth0 chain (same reasoning as `tenantCacheKey.ts`).
 */

const readText = (blob: Blob): Promise<string> => {
    if (typeof blob.text === 'function') return blob.text();
    // jsdom's Blob predates `text()`; FileReader is in both.
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });
};

export const parseBlobErrorBody = async (error: unknown): Promise<unknown> => {
    const data = (error as { data?: unknown } | null)?.data;
    if (typeof Blob === 'undefined' || !(data instanceof Blob)) return error;
    let text: string;
    try {
        text = await readText(data);
    } catch {
        return error;
    }
    return { ...(error as object), data: refusalOf(text) };
};

/** Our refusal body, parsed; `undefined` for anything that is not one. */
const refusalOf = (text: string): Record<string, unknown> | undefined => {
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return undefined;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
    const body = parsed as Record<string, unknown>;
    return ['reason', 'code', 'detail'].some((key) => typeof body[key] === 'string' && body[key] !== '')
        ? body
        : undefined;
};
